import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { Summary, SummaryId } from './past-meetings-detail.types.ts';
import '@/assets/styles/views/coach/past-meetings-detail-v2.scss';
import LineDot from '@/assets/svg/line-dot.svg?react';
import clsx from 'clsx';
import { convertMeetingType, formatMilliseconds } from '@/utility';
import { useLocation, useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';
import { LoadingSpinner, ReportModal } from '@/components';
import StudentRatingModal from '@/components/student-rating-modal';
import { useApi } from '@/api/useApi.tsx';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { toast } from 'react-toastify';
import { useQueryClient } from 'react-query';
import {
	CoachExpertInteractionFeedbackUpdateRequest,
	CoachExpertInteractionFeedbackCreateRequest,
	CoachExpertInteractionFeedbackCreateRequestConnectionStatusEnum as ConnectionStatusEnum,
	BackupRoomStudentInvitationRequest
} from '@dopingtech/live_expert_bff_api_ts';
import { CustomPlaceholder } from '@/components/custom-placeholder/CustomPlaceholder.tsx';
import { InteractionDetailResponseInteractionStatusEnum } from '@dopingtech/live_expert_bff_api_ts/api.ts';
import dayjs from 'dayjs';
import { useSizes } from '@/utility/responsive.ts';
import CircleLink from '@/assets/svg/link-circle-blue.svg';
import { useFeatureFlag } from 'configcat-react';
import { INPUT_MAX_LENGTH } from '@/constants';
import MeetingInteractionFeedback from '@/components/meeting-interaction-feedback';
import { Choose, If, Otherwise, When } from 'vite-plugin-react-control-statements';
import MeetReportFormV2 from '@/components/meet-report-form/index-v2.tsx';

const MAX_CONTENT_LENGTH = INPUT_MAX_LENGTH.FEEDBACK;

const summaries: Array<Summary> = [
	{
		id: 'commentToStudent',
		title: 'Öğrenci Özeti',
		description: 'Öğrenciniz ile yaptığınız görüşmeye dair ödevlerinizi yazın.',
		placeholder: 'Görüşme detaylarını buraya girin...',
		icon: <i className="icons-chat-line" />
	},
	{
		id: 'commentToParent',
		title: 'Veli Özeti',
		description: 'Öğrencinizin velisi için yaptığınız görüşmenin değerlendirmesini girin.',
		placeholder: 'Veli görüşmesi detaylarını buraya girin...',
		icon: <i className="icons-users-group-rounded" />
	},
	{
		id: 'commentToBackoffice',
		title: 'Operasyon Özeti',
		description: 'Öğrenciyle yaptığınız görüşmeye dair genel değerlendirmenizi, yaşadığınız bir sorun ya da iletmek istediğiniz ek konuları bu alana yazabilirsiniz.',
		placeholder: 'Operasyon detaylarını buraya girin...',
		icon: <i className="icons-checklist-minimalistic" />
	}
];

const statusOptions = [
	{
		value: ConnectionStatusEnum.Completed,
		label: 'Gerçekleşti',
		dropdownLabel: 'Gerçekleşti',
		className: ''
	},
	{
		value: ConnectionStatusEnum.CompletedWithMinorConnectionIssues,
		label: 'Gerçekleşti',
		dropdownLabel: 'Gerçekleşti - Bağlantı Sorunu Yaşandı',
		className: ''
	},
	{
		value: ConnectionStatusEnum.Failed,
		label: 'Gerçekleşmedi',
		dropdownLabel: 'Gerçekleşmedi',
		className: 'failed'
	}
];

const MeetingDetailsV2: React.FC = () => {
	const { state } = useLocation();
	const queryClient = useQueryClient();
	const navigate = useNavigate();
	const { FeedbackControllerApi, InteractionControllerApi, BackupRoomControllerApi } = useApi();

	const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
	// ponytail: guards against overlapping onChangeStatus calls when the status is clicked repeatedly in quick succession
	const isChangingStatusRef = useRef(false);
	const [isReportModalOpen, setIsReportModalOpen] = useState(false);
	const [isStudentRatingModalOpen, setStudentRatingModalOpen] = useState(false);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [editMode, setEditMode] = useState<boolean>(false);
	const { isMobile } = useSizes();
	const [status, setStatus] = useState<ConnectionStatusEnum>(ConnectionStatusEnum.Completed);
	const [comments, setComments] = useState<Partial<Record<SummaryId, string>>>({});

	const sendBackupRoomInvitationToStudentMutation = useMutationProvider((data: BackupRoomStudentInvitationRequest) =>
		BackupRoomControllerApi.sendBackupRoomInvitationToStudent({
			meetingId: data?.meetingId,
			studentId: data?.studentId
		})
	);

	const { value: isActiveFromOutsideMeetLink } = useFeatureFlag('isActiveFromOutsideMeetLink', false);

	const [isSendedBackupLink, setIsSendedBackupLink] = useState(false);

	const {
		data: interactionFeedback,
		isLoading: interactionFeedbackLoading,
		refetch: refetchInteractionFeedback
	} = useQueryProvider([QUERY_KEYS.findInteractionById, state?.interactionId], () => InteractionControllerApi.findInteractionById(state?.interactionId), {
		select: ({ data }) => data?.data
	});

	const isRefunded = useMemo(() => Boolean(interactionFeedback?.isManualRefund || interactionFeedback?.isSystemRefund), [interactionFeedback]);

	const { data: coachExpertInteractionFeedbackData, refetch: refetchCoachExpertInteractionFeedback } = useQueryProvider(
		[QUERY_KEYS.findCoachExpertInteractionFeedbackByInteractionId, state?.interactionId],
		() => FeedbackControllerApi.findCoachExpertInteractionFeedbackByInteractionId(state?.interactionId),
		{
			select: ({ data }) => data?.data
		}
	);

	const saveFeedback = useMutationProvider(
		({ id, feedBack }: { id: number; feedBack: CoachExpertInteractionFeedbackUpdateRequest }) => FeedbackControllerApi.updateCoachExpertInteractionFeedback(id, feedBack),
		{
			onSuccess: () => {
				queryClient.invalidateQueries([QUERY_KEYS.findCoachExpertInteractionFeedbackByInteractionId]);
				toast.success('Geri bildirim başarıyla kaydedildi.');
			},
			onError: () => {
				setIsLoading(false);
			}
		}
	);

	const createFeedback = useMutationProvider((body: CoachExpertInteractionFeedbackCreateRequest) => FeedbackControllerApi.createCoachExpertInteractionFeedback(body), {
		onSuccess: () => {
			queryClient.invalidateQueries([QUERY_KEYS.findCoachExpertInteractionFeedbackByInteractionId]);
			toast.success('Geri bildirim başarıyla kaydedildi.');
		},
		onError: () => {
			setIsLoading(false);
		}
	});

	const converStatus = (interactionStatus: InteractionDetailResponseInteractionStatusEnum | undefined) => {
		switch (interactionStatus) {
			case InteractionDetailResponseInteractionStatusEnum.MeetingEnded:
			case InteractionDetailResponseInteractionStatusEnum.MeetingEndedSuccessfully:
				return ConnectionStatusEnum.Completed;
			case InteractionDetailResponseInteractionStatusEnum.MeetingFailed:
			case InteractionDetailResponseInteractionStatusEnum.MeetingFailedWithConnectionProblems:
				return ConnectionStatusEnum.Failed;
			case InteractionDetailResponseInteractionStatusEnum.MeetingEndedWithConnectionProblems:
				return ConnectionStatusEnum.CompletedWithMinorConnectionIssues;
			default:
				return ConnectionStatusEnum.Failed;
		}
	};

	useEffect(() => {
		if (coachExpertInteractionFeedbackData) {
			setComments({
				commentToStudent: coachExpertInteractionFeedbackData?.commentToStudent,
				commentToParent: coachExpertInteractionFeedbackData?.commentToParent,
				commentToBackoffice: coachExpertInteractionFeedbackData?.commentToBackoffice
			});
		}
	}, [coachExpertInteractionFeedbackData]);

	useEffect(() => {
		if (interactionFeedback?.interactionStatus) {
			setStatus(converStatus(interactionFeedback.interactionStatus));
		}
	}, [interactionFeedback]);

	const onSave = () => {
		setIsLoading(true);

		handleSave().finally(() => {
			setIsLoading(false);
			setEditMode(false);
		});
	};

	const handleSave = async (): Promise<void> => {
		const feedBack: CoachExpertInteractionFeedbackUpdateRequest = {
			commentToStudent: comments?.commentToStudent,
			commentToParent: comments?.commentToParent,
			commentToBackoffice: comments?.commentToBackoffice,
			...(coachExpertInteractionFeedbackData?.id ? null : { connectionStatus: status })
		};

		if (coachExpertInteractionFeedbackData?.id) {
			await saveFeedback.mutateAsync({
				id: interactionFeedback?.feedbackId as number,
				feedBack: feedBack
			});
		} else {
			await createFeedback.mutateAsync({
				...feedBack,
				interactionId: state?.interactionId
			});
		}

		await refetchInteractionFeedback();
		await refetchCoachExpertInteractionFeedback();
	};

	const onEditing = (id: SummaryId, content: string) => {
		if (content?.length <= MAX_CONTENT_LENGTH) {
			setComments(prev => ({
				...prev,
				[id]: content
			}));
		}
	};

	const onSendLinkToStudent = async () => {
		const entity: BackupRoomStudentInvitationRequest = {};

		entity.studentId = interactionFeedback?.studentId;

		const result = await sendBackupRoomInvitationToStudentMutation.mutateAsync(entity);

		if (result) {
			setIsSendedBackupLink(true);

			setTimeout(() => {
				setIsSendedBackupLink(false);
			}, 2000);
		}

		toast.success('Görüşme linki öğrenciye gönderildi!');
	};

	const onChangeStatus = async (newStatus: ConnectionStatusEnum) => {
		if (isChangingStatusRef.current) return;

		isChangingStatusRef.current = true;
		setIsLoading(true);

		try {
			const feedBack: CoachExpertInteractionFeedbackUpdateRequest = {
				connectionStatus: newStatus
			};

			if (interactionFeedback?.feedbackId) {
				await saveFeedback.mutateAsync({
					id: interactionFeedback?.feedbackId,
					feedBack: feedBack
				});
			} else {
				await createFeedback.mutateAsync({
					...feedBack,
					interactionId: state?.interactionId
				});
			}

			await queryClient.refetchQueries([QUERY_KEYS.findInteractionById, state?.interactionId]);

			await refetchInteractionFeedback();

			setStatus(newStatus);

			setIsStatusDropdownOpen(false);
		} finally {
			isChangingStatusRef.current = false;
			setIsLoading(false);
		}
	};

	const onBack = () => {
		navigate(NavigationPath.coachPastMeetings);
	};

	const onNavigateStudentCard = () => {
		navigate(NavigationPath.coachStudentDetails, {
			state: {
				studentId: interactionFeedback?.studentId,
				openMeetingsTab: true
			}
		});
	};

	const onNavigateMessages = () => {
		navigate(NavigationPath.messages, {
			state: {
				studentId: interactionFeedback?.studentId
			}
		});
	};

	const isConnectionIssues = status === ConnectionStatusEnum.Failed || status === ConnectionStatusEnum.CompletedWithMinorConnectionIssues;

	const currentStatusLabel = useMemo(() => {
		return statusOptions.find(option => option.value === status)?.label || 'Gerçekleşti';
	}, [status]);

	const shouldShowSummaries = status !== ConnectionStatusEnum.Failed;

	return (
		<div className="meeting-details">
			<header className="meeting-details__header">
				<a
					href="#"
					className="meeting-details__header-back"
					onClick={onBack}>
					<i className="icons-arrow-left" />
				</a>
				<div className="meeting-details__header-profile-image">
					<CustomPlaceholder
						ready={!interactionFeedbackLoading}
						type="round">
						<img
							src={interactionFeedback?.studentAvatarUrl}
							alt={interactionFeedback?.studentName}
						/>
					</CustomPlaceholder>
				</div>
				<div className="meeting-details__header-info">
					<CustomPlaceholder
						ready={!interactionFeedbackLoading}
						type="rect"
						rows={2}
						width={80}
						height={30}>
						<div className="meeting-details__header-info-name">{`${interactionFeedback?.studentName} ${interactionFeedback?.studentSurname?.[0]}.`}</div>
						<div className="meeting-details__header-info-grade">{interactionFeedback?.studentGradeName}</div>
					</CustomPlaceholder>
				</div>
				<div className="meeting-details__header-actions">
					<button
						className="button-profile-card"
						onClick={onNavigateStudentCard}>
						<i className="icons-user" />
						{!isMobile && 'Profil Kartını Görüntüle'}
					</button>
					{interactionFeedback?.meetingType === 'APPOINTMENT' && (
						<button
							className="meeting-details__header-actions-message"
							onClick={onNavigateMessages}>
							<i className="icons-letter" />
							{!isMobile && 'Mesaj'}
						</button>
					)}

					{status !== ConnectionStatusEnum.Failed && (
						<button
							className="meeting-details__header-actions-report"
							onClick={() => setIsReportModalOpen(true)}>
							<i className="icons-danger-circle" />
							{!isMobile && 'Raporla'}
						</button>
					)}
				</div>
			</header>

			<div className="meeting-details__status">
				<CustomPlaceholder
					ready={!interactionFeedbackLoading}
					type="rect"
					height={26}
					className="meeting-details__status-left-side">
					<div className="meeting-details__status-left-side">
						<div className="meeting-details__status-dropdown">
							<button
								disabled={isLoading}
								className={clsx('meeting-details__status-dropdown-button', {
									'meeting-details__status-dropdown-button--failed': status === ConnectionStatusEnum.Failed,
									'meeting-details__status-dropdown-button--connection-issue': status === ConnectionStatusEnum.CompletedWithMinorConnectionIssues
								})}
								onClick={() => setIsStatusDropdownOpen(!isStatusDropdownOpen)}>
								{currentStatusLabel}
								{isStatusDropdownOpen ? <i className="icons-alt-arrow-up" /> : <i className="icons-alt-arrow-down" />}
							</button>
							{isStatusDropdownOpen && (
								<div className="meeting-details__status-dropdown-content">
									{statusOptions
										.filter(option => option.value !== status)
										.map(option => (
											<button
												key={option.value}
												onClick={() => onChangeStatus(option.value)}
												className={option.className}>
												{option.dropdownLabel}
											</button>
										))}
								</div>
							)}
						</div>

						{isConnectionIssues && (
							<>
								<LineDot
									className="meeting-details__status-separator"
									color="var(--danger-text-strong)"
								/>

								<span className="meeting-details__status-issue failed">Bağlantı Sorunu Yaşandı</span>
							</>
						)}
					</div>
				</CustomPlaceholder>
				<CustomPlaceholder
					ready={!interactionFeedbackLoading}
					type="rect"
					height={26}
					width={135}
					className="meeting-details__status-rating">
					<div className="meeting-details__status-rating">
						{isActiveFromOutsideMeetLink && interactionFeedback?.isActiveMeeting && (
							<div
								className={clsx('connect-alternative-link', {
									disabled: sendBackupRoomInvitationToStudentMutation?.isLoading || isSendedBackupLink
								})}
								onClick={e => {
									onSendLinkToStudent();
									e.stopPropagation();
								}}>
								<img src={CircleLink} />
								{isSendedBackupLink ? 'Link gönderildi' : 'Öğrenciye Link Gönder'}
							</div>
						)}

						<div className="meeting-details__status-rating-top">
							{isRefunded && <span className="meeting-details__status-rating-refund">Kredi İade Edildi</span>}
							{status !== ConnectionStatusEnum.Failed && interactionFeedback?.commentToExpert && (
								<span onClick={() => interactionFeedback?.rating && setStudentRatingModalOpen(true)}>
									<i className="icons-chat-line" />
									{interactionFeedback?.commentToExpert ? 1 : 0}
								</span>
							)}
							<div
								className={clsx('meeting-details__status-rating-stars', {
									clickable: status !== ConnectionStatusEnum.Failed && interactionFeedback?.rating
								})}
								onClick={() => status !== ConnectionStatusEnum.Failed && interactionFeedback?.rating && setStudentRatingModalOpen(true)}>
								{[1, 2, 3, 4, 5].map(star => (
									<i
										key={star}
										className={clsx(
											'icons-star',
											star <= (interactionFeedback?.rating || 0)
												? clsx('filled', {
														red: (interactionFeedback?.rating || 0) < 3,
														orange: interactionFeedback?.rating === 3
													})
												: ''
										)}
									/>
								))}
							</div>
						</div>
						{status !== ConnectionStatusEnum.Failed ? (
							<span className="meeting-details__status-rating-label">{interactionFeedback?.rating ? 'Öğrenci Puanı' : 'Henüz Puanlamadı'}</span>
						) : (
							<span className="meeting-details__status-rating-label red">Sistem Puanı</span>
						)}
					</div>
				</CustomPlaceholder>
			</div>

			<div className="meeting-details__info">
				<div className="meeting-details__info-item left-side">
					<i className="icons-calendar" />
					<div className="meeting-details__info-item-label">Görüşme Tarihi</div>
					<div className="meeting-details__info-item-value">{dayjs(interactionFeedback?.interactionStartDate).format('D MMMM, HH:mm')}</div>
				</div>
				<div className="meeting-details__info-vertical-seperator" />
				<div className="meeting-details__info-item">
					<i className="icons-alarm" />
					<div className="meeting-details__info-item-label">Görüşme Süresi</div>
					<div className="meeting-details__info-item-value">{formatMilliseconds(interactionFeedback?.meetingDuration as any)}</div>
				</div>
				<div className="meeting-details__info-item">
					<i className="icons-extend-call" />
					<div className="meeting-details__info-item-label">Katılım Süreniz</div>
					<div className="meeting-details__info-item-value">{formatMilliseconds(interactionFeedback?.expertMeetingDuration || ('0' as any))}</div>
				</div>
				<div className="meeting-details__info-vertical-seperator" />
				<div className="meeting-details__info-item left-side">
					<i className="icons-videocamera-record" />
					<div className="meeting-details__info-item-label">Görüşme Türü</div>
					<div className="meeting-details__info-item-value">{convertMeetingType(interactionFeedback?.meetingType, 'APPOINTMENT')}</div>
				</div>
				<div className="meeting-details__info-vertical-seperator" />
				<div className="meeting-details__info-item">
					<i className="icons-document" />
					<div className="meeting-details__info-item-label">Görüşme Konusu</div>
					<div className="meeting-details__info-item-value">{interactionFeedback?.meetingSubjectName}</div>
				</div>
			</div>

			<If condition={interactionFeedback?.meetingType === 'APPOINTMENT'}>
				<MeetingInteractionFeedback
					connectionStatus={converStatus(interactionFeedback?.interactionStatus)}
					interactionId={state?.interactionId}
					interactionFeedback={interactionFeedback}
				/>
			</If>

			<If condition={shouldShowSummaries}>
				<div className="meeting-details-wrapper">
					<p className="meeting-details-description">Yaptığınız görüşmeye ilişkin özetleri ilgili alanlara yazınız.</p>
					{summaries.map(summary => {
						return (
							<CustomPlaceholder
								ready={!interactionFeedbackLoading}
								type="rect"
								key={summary.id}
								height={66}
								className="meeting-details__summary">
								<div
									key={summary.id}
									className="meeting-details__summary">
									<div className="meeting-details__summary-header">
										<div className="meeting-details__summary-header-icon">{summary.icon}</div>
										<div className="meeting-details__summary-header-content">
											<div className="meeting-details__summary-header-title">{summary.title}</div>
											<div className="meeting-details__summary-header-description">{summary.description}</div>
										</div>
									</div>
									<div className="meeting-details-input-wrapper">
										<textarea
											disabled={
												editMode
													? false
													: !!(
															coachExpertInteractionFeedbackData?.commentToStudent ||
															coachExpertInteractionFeedbackData?.commentToParent ||
															coachExpertInteractionFeedbackData?.commentToBackoffice
														)
											}
											className="meeting-details-input"
											placeholder={summary.placeholder}
											defaultValue={coachExpertInteractionFeedbackData?.[summary.id] || ''}
											maxLength={MAX_CONTENT_LENGTH}
											onChange={e => onEditing(summary.id, e.target.value)}
										/>
									</div>
								</div>
							</CustomPlaceholder>
						);
					})}
					<div className={clsx('meeting-details-actions-wrapper', {'mobile': isMobile})}>
						<Choose>
							<When
								condition={(!editMode && !!(coachExpertInteractionFeedbackData?.commentToStudent || coachExpertInteractionFeedbackData?.commentToParent || coachExpertInteractionFeedbackData?.commentToBackoffice))}>
								<button className={clsx('save edit', {'mobile': isMobile})}
										onClick={() => setEditMode(true)}>
									<i className="icons-pen"/>
									Düzenle
								</button>
							</When>
							<Otherwise>
								<button
									className={clsx('save', {'mobile': isMobile})}
									disabled={(!(
										comments?.commentToStudent?.length ||
										comments?.commentToParent?.length ||
										comments?.commentToBackoffice?.length
									) || (
										(coachExpertInteractionFeedbackData?.commentToStudent === comments?.commentToStudent) &&
										(coachExpertInteractionFeedbackData?.commentToParent === comments?.commentToParent) &&
										(coachExpertInteractionFeedbackData?.commentToBackoffice === comments?.commentToBackoffice)
									) as boolean)}
									onClick={onSave}
								>
									<i className="icons-diskette"/>
									Kaydet
								</button>
							</Otherwise>
						</Choose>
					</div>
				</div>
			</If>

			<MeetReportFormV2
				defaultOpen={status === ConnectionStatusEnum.Failed}
				interactionId={state?.interactionId}
			/>

			<ReportModal
				isOpen={isReportModalOpen}
				onClose={() => {
					setIsReportModalOpen(false);
				}}
				studentId={interactionFeedback?.studentId}
				interactionId={state?.interactionId}
			/>
			<If condition={!!interactionFeedback}>
				<StudentRatingModal
					isOpen={isStudentRatingModalOpen}
					onClose={() => {
						setStudentRatingModalOpen(false);
					}}
					interactionFeedback={interactionFeedback}
				/>
			</If>
			<If condition={isLoading}>
				<LoadingSpinner loadingText="Kaydediliyor"/>
			</If>
		</div>
	);
};

export default MeetingDetailsV2;
